import { memo } from 'react'

import {
  AssetInformation,
  ProgressContainer,
  RadioTab,
  Section,
  SimpleModal,
  Space,
  TabStrip,
  ValidationMessage
} from '../../../../../components/core-v2'
import { StrategyType } from '../../../types'
import { CollateralAlert } from '../../../util/Alerts'
import { WizardButtons } from '../../00-builder/wizard-buttons'
import { Delta1Editor } from '../organisms/delta1'
import { LeverageEditor } from '../organisms/leverage'
import { ProtectionEditor } from '../organisms/protection/index'
import { RiskManagedEditor } from '../organisms/risk-managed'
import { StrategyStepState } from '../state/strategy-slice'

type StrategyEditorProps = {
  canMoveNext: boolean
  onCloseInspection: () => void
  onMoveBack: () => void
  onMoveNext: () => void
  onSetType: (type: StrategyType) => void
  state: StrategyStepState
}

function StrategyEditorInner({
  canMoveNext,
  onCloseInspection,
  onMoveBack,
  onMoveNext,
  onSetType,
  state: stepState
}: StrategyEditorProps) {
  const selectedStrategyState = stepState.selectedStrategy

  return (
    <>
      <Section data-testid="strategy-editor" mt={6} type="primary">
        <TabStrip>
          {selectedStrategyState.delta1.isAllowed && (
            <RadioTab
              data-testid="tab-delta1"
              isSelected={selectedStrategyState.type === 'delta1'}
              onClick={() => onSetType('delta1')}
            >
              Delta One
            </RadioTab>
          )}
          {selectedStrategyState.riskManaged.isAllowed && (
            <RadioTab
              data-testid="tab-riskmanaged"
              isSelected={selectedStrategyState.type === 'riskmanaged'}
              onClick={() => onSetType('riskmanaged')}
            >
              Risk managed
            </RadioTab>
          )}
          {(selectedStrategyState.leverage.constantProportion.isAllowed ||
            selectedStrategyState.leverage.volControl.isAllowed) && (
            <RadioTab
              data-testid="tab-leverage"
              isSelected={selectedStrategyState.type === 'leverage'}
              onClick={() => onSetType('leverage')}
            >
              Leverage
            </RadioTab>
          )}
          {(selectedStrategyState.protection.continuous.isAllowed ||
            selectedStrategyState.protection.term.isAllowed) && (
            <RadioTab
              data-testid="tab-protection"
              isSelected={selectedStrategyState.type === 'protection'}
              onClick={() => onSetType('protection')}
            >
              Protection
            </RadioTab>
          )}
        </TabStrip>
        {stepState.assetBeingInspected && (
          <SimpleModal>
            <ProgressContainer
              currentStep={1}
              numSteps={1}
              title="Asset Details"
              onClickClose={onCloseInspection}
            >
              <Space isflex flexDirection="column">
                <AssetInformation asset={stepState.assetBeingInspected as any} />
              </Space>
            </ProgressContainer>
          </SimpleModal>
        )}

        <>
          {selectedStrategyState.type === 'delta1' && selectedStrategyState.delta1.isAllowed && (
            <Delta1Editor />
          )}
          {selectedStrategyState.type === 'riskmanaged' &&
            selectedStrategyState.riskManaged.isAllowed && <RiskManagedEditor />}
          {selectedStrategyState.type === 'leverage' &&
            (selectedStrategyState.leverage.constantProportion.isAllowed ||
              selectedStrategyState.leverage.volControl.isAllowed) && <LeverageEditor />}
          {selectedStrategyState.type === 'protection' &&
            (selectedStrategyState.protection.continuous.isAllowed ||
              selectedStrategyState.protection.term.isAllowed) && <ProtectionEditor />}
          <ValidationMessage
            key="strategy_editor_errors"
            errors={stepState.errors}
            excludeFieldNames={[
              'maturityDate',
              'leverage_volControl_protectionAsset',
              'riskManaged_protectionAsset'
            ]}
          />
          <ValidationMessage
            key="builder_errors"
            errors={stepState.builderErrors}
            excludeFieldNames={['collateral']}
          />
        </>
      </Section>
      <CollateralAlert />
      <WizardButtons
        canMoveBack
        canMoveNext={canMoveNext}
        onMoveBack={onMoveBack}
        onMoveNext={onMoveNext}
      />
    </>
  )
}

const areEqual = (ap: StrategyEditorProps, bp: StrategyEditorProps) => {
  const a = ap.state.selectedStrategy
  const b = bp.state.selectedStrategy

  return (
    a.delta1 === b.delta1 &&
    a.riskManaged === b.riskManaged &&
    a.leverage === b.leverage &&
    a.protection === b.protection &&
    a.type === b.type &&
    ap.state.canProceed === bp.state.canProceed &&
    ap.state.assetBeingInspected === bp.state.assetBeingInspected &&
    ap.state.errors === bp.state.errors &&
    ap.state.builderErrors === bp.state.builderErrors
  )
}

export const StrategyEditor = memo(StrategyEditorInner, areEqual)
